import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  message,
  Space,
  Spin,
  Steps,
  Typography,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SignInButton } from '../../../../SignInButton';
import { UserContext } from '../../../../UserContext';
import { ClientNavigation } from '../../../../components/client/client-navigation';
import { AuthorizedRepForm } from '../../../../components/twilio-onboarding/AuthorizedRepForm';
import { BusinessInfoForm } from '../../../../components/twilio-onboarding/BusinessInfoForm';
import { CampaignInfoForm } from '../../../../components/twilio-onboarding/CampaignInfoForm';
import { ComplianceCheckPanel } from '../../../../components/twilio-onboarding/ComplianceCheckPanel';
import { OnboardingStatusTracker } from '../../../../components/twilio-onboarding/OnboardingStatusTracker';
import { RejectionRecoveryPanel } from '../../../../components/twilio-onboarding/RejectionRecoveryPanel';
import { ScreenshotUpload } from '../../../../components/twilio-onboarding/ScreenshotUpload';
import {
  ComplianceCheckRequest,
  ComplianceResult,
  TwilioOnboardingConfig,
  TwilioOnboardingStatus,
} from '../../../../types/twilio-onboarding';
import { ClientConfig as ClientConfigType } from '../../../../types';
import { CrmType } from '../../../../types/crmTypes';
import {
  addInboundNumber,
  fetchHubSpotOnboardingInfo,
  fetchOnboardingStatus,
  runComplianceCheck,
  startOnboarding,
} from '../../../../utils/twilio-onboarding-api';
import { HubSpotMlsInfo } from '../../../../types/twilio-onboarding';
import {
  computeNextStep,
  computePrevStep,
  determinePageMode,
  ensureProtocol,
  formatPhoneE164,
  getFieldsForStep,
  getNextButtonLabel,
  stepRequiresValidation,
  WIZARD_STEPS,
} from '../../../../components/twilio-onboarding/onboarding-logic';

const { Title, Paragraph, Text } = Typography;

export function TwilioOnboardingPage({ apiUrl }: { apiUrl: string }) {
  const { clientId } = useParams();
  const user = useContext(UserContext);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [clientConfig, setClientConfig] = useState<
    ClientConfigType | undefined
  >();
  const [onboardingStatus, setOnboardingStatus] =
    useState<TwilioOnboardingStatus | null>(null);
  const [mode, setMode] = useState<'setup' | 'monitor'>('setup');

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceResult, setComplianceResult] =
    useState<ComplianceResult | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | undefined>();
  const [inboundLeadSource, setInboundLeadSource] = useState('');
  const [inboundAreaCode, setInboundAreaCode] = useState('');
  const [addingNumber, setAddingNumber] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [mlsInfo, setMlsInfo] = useState<HubSpotMlsInfo | null>(null);

  const numericClientId = clientId ? parseInt(clientId, 10) : undefined;

  useEffect(() => {
    void loadInitialData();
  }, [user?.accessToken, clientId]);

  const loadInitialData = async () => {
    if (!user || !clientId) {
      return;
    }
    try {
      setLoading(true);
      setError(undefined);
      setMlsInfo(null);

      const [configResponse, status] = await Promise.all([
        fetch(`${apiUrl}/api/settings/${clientId}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        }),
        fetchOnboardingStatus(apiUrl, user.accessToken, clientId),
      ]);

      if (configResponse.ok) {
        const data = await configResponse.json();
        setClientConfig(data.config);
        if (
          data.config?.friendly_name &&
          !form.getFieldValue(['business_info', 'brand_name'])
        ) {
          form.setFieldValue(
            ['business_info', 'brand_name'],
            data.config.friendly_name,
          );
        }
      }

      setOnboardingStatus(status);
      setMode(determinePageMode(status));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const validateCurrentStep = async (): Promise<boolean> => {
    try {
      const fields = getFieldsForStep(currentStep);
      if (fields) {
        await form.validateFields(fields);
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleNext = async () => {
    if (stepRequiresValidation(currentStep)) {
      const valid = await validateCurrentStep();
      if (!valid) {
        return;
      }
    }
    setCurrentStep(s => computeNextStep(s));
  };

  const handlePrev = () => {
    setCurrentStep(s => computePrevStep(s));
  };

  const handleComplianceCheck = async () => {
    if (!user) {
      return;
    }
    const values = form.getFieldsValue(true);
    const campaignInfo = values.campaign_info;
    if (!campaignInfo?.form_url) {
      message.error('Please fill in the Campaign Configuration step first.');
      return;
    }
    setComplianceLoading(true);
    try {
      const params: ComplianceCheckRequest = {
        form_url: ensureProtocol(campaignInfo.form_url),
        client_id: numericClientId,
        trigger_text: campaignInfo.form_button_text,
        privacy_policy_url: ensureProtocol(campaignInfo.privacy_policy_url),
        terms_url: ensureProtocol(campaignInfo.terms_url),
        screenshot_url: screenshotUrl || undefined,
      };
      const result = await runComplianceCheck(apiUrl, user.accessToken, params);
      setComplianceResult(result);
      if (result.screenshot_url && !screenshotUrl) {
        setScreenshotUrl(result.screenshot_url);
        form.setFieldValue(
          ['campaign_info', 'screenshot_url'],
          result.screenshot_url,
        );
      }
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Compliance check failed',
      );
    } finally {
      setComplianceLoading(false);
    }
  };

  const handlePrefillFromHubSpot = async () => {
    if (!user || !clientId) {
      return;
    }
    setPrefilling(true);
    try {
      const info = await fetchHubSpotOnboardingInfo(
        apiUrl,
        user.accessToken,
        clientId,
      );
      if (!info) {
        message.warning('No HubSpot company linked to this client.');
        return;
      }
      const bi = info.business_info;
      const businessCandidates: Record<string, string> = {};

      if (bi.legal_entity_name) {
        businessCandidates.legal_entity_name = bi.legal_entity_name;
      }
      if (bi.ein) {
        businessCandidates.ein = bi.ein;
      }
      if (bi.business_website) {
        businessCandidates.business_website = ensureProtocol(
          bi.business_website,
        );
      }
      if (bi.street_address) {
        businessCandidates.street_address = bi.street_address;
      }
      if (bi.city) {
        businessCandidates.city = bi.city;
      }
      if (bi.state) {
        businessCandidates.state = bi.state;
      }
      if (bi.postal_code) {
        businessCandidates.postal_code = bi.postal_code;
      }

      const repCandidates: Record<string, string> = {};
      if (bi.first_name) {
        repCandidates.first_name = bi.first_name;
      }
      if (bi.last_name) {
        repCandidates.last_name = bi.last_name;
      }
      if (bi.email) {
        repCandidates.email = bi.email;
      }
      if (bi.phone) {
        repCandidates.phone = formatPhoneE164(bi.phone);
      }
      if (bi.title) {
        repCandidates.business_title = bi.title;
      }

      const hasMls = Object.values(info.mls_info).some(Boolean);
      setMlsInfo(hasMls ? info.mls_info : null);

      if (
        Object.keys(businessCandidates).length === 0 &&
        Object.keys(repCandidates).length === 0
      ) {
        message.info(
          hasMls
            ? 'No business fields to prefill — MLS information has been loaded.'
            : 'HubSpot has no business information to prefill.',
        );
        return;
      }

      const fieldsToValidate: string[][] = [];

      const currentBusiness = form.getFieldValue('business_info') ?? {};
      const businessToSet: Record<string, string> = {};
      for (const [key, val] of Object.entries(businessCandidates)) {
        if (!currentBusiness[key]) {
          businessToSet[key] = val;
          fieldsToValidate.push(['business_info', key]);
        }
      }
      if (Object.keys(businessToSet).length > 0) {
        form.setFieldsValue({
          business_info: { ...currentBusiness, ...businessToSet },
        });
      }

      const currentRep = form.getFieldValue('authorized_rep') ?? {};
      const repToSet: Record<string, string> = {};
      for (const [key, val] of Object.entries(repCandidates)) {
        if (!currentRep[key]) {
          repToSet[key] = val;
          fieldsToValidate.push(['authorized_rep', key]);
        }
      }
      if (Object.keys(repToSet).length > 0) {
        form.setFieldsValue({
          authorized_rep: { ...currentRep, ...repToSet },
        });
      }

      // AuthorizedRepForm is unmounted while on this step, so Ant Design has
      // no registered fields to validate under `authorized_rep` — only
      // validate the business_info fields, which are mounted here.
      const mountedFieldsToValidate = fieldsToValidate.filter(
        ([section]) => section === 'business_info',
      );
      const hasUnvalidatedRepFields = fieldsToValidate.some(
        ([section]) => section === 'authorized_rep',
      );

      if (mountedFieldsToValidate.length > 0) {
        try {
          await form.validateFields(mountedFieldsToValidate);
          message.success(
            hasUnvalidatedRepFields
              ? 'Fields prefilled from HubSpot. Review the Authorized Representative step before continuing.'
              : 'Fields prefilled from HubSpot. Review and complete remaining fields.',
          );
        } catch {
          message.warning(
            'Some prefilled fields have invalid values — review the highlighted fields before continuing.',
          );
        }
      } else if (hasUnvalidatedRepFields) {
        message.info(
          'Authorized representative fields prefilled from HubSpot. Review them on the next step before continuing.',
        );
      } else {
        message.info(
          hasMls
            ? 'All prefillable fields are already filled in — MLS information has been loaded.'
            : 'All prefillable fields are already filled in.',
        );
      }
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Failed to fetch HubSpot data',
        8,
      );
    } finally {
      setPrefilling(false);
    }
  };

  const handleSubmit = async () => {
    if (!user || !numericClientId) {
      return;
    }
    const values = form.getFieldsValue(true);
    const requiredFields = [
      ['business_info', 'legal_entity_name'],
      ['business_info', 'ein'],
      ['authorized_rep', 'email'],
      ['authorized_rep', 'phone'],
      ['campaign_info', 'form_url'],
      ['campaign_info', 'client_website'],
    ];
    const missing = requiredFields.some(
      ([section, field]) => !values[section]?.[field],
    );
    if (missing) {
      message.error('Please complete all required steps before submitting.');
      return;
    }
    const disclosureText = values.campaign_info?.disclosure_text;
    if (!disclosureText) {
      message.warning(
        "Disclosure text is empty. A default will be used, but providing the actual text from the client's form is recommended.",
      );
    }

    const config: TwilioOnboardingConfig = {
      business_info: {
        ...values.business_info,
        business_website: ensureProtocol(
          values.business_info?.business_website,
        ),
      },
      authorized_rep: values.authorized_rep,
      campaign_info: {
        ...values.campaign_info,
        client_website: ensureProtocol(values.campaign_info?.client_website),
        form_url: ensureProtocol(values.campaign_info?.form_url),
        listing_url: ensureProtocol(values.campaign_info?.listing_url),
        privacy_policy_url: ensureProtocol(
          values.campaign_info?.privacy_policy_url,
        ),
        terms_url: ensureProtocol(values.campaign_info?.terms_url),
        screenshot_url: screenshotUrl || values.campaign_info?.screenshot_url,
      },
      area_code: values.area_code,
    };

    setSubmitting(true);
    try {
      const result = await startOnboarding(apiUrl, user.accessToken, {
        client_id: numericClientId,
        config,
      });
      message.success('Onboarding started successfully!');
      setOnboardingStatus(result);
      setMode('monitor');
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Failed to start onboarding',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderMlsInfoSection = (mls: HubSpotMlsInfo) => {
    const hasAny = Object.values(mls).some(
      v => v !== null && v !== undefined && v !== '',
    );
    if (!hasAny) {
      return null;
    }
    return (
      <Card style={{ marginTop: 24 }} title="MLS Information (from HubSpot)">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px 24px',
          }}
        >
          {mls.primary_mls && (
            <>
              <Text strong>Primary MLS:</Text>
              <Text>{mls.primary_mls}</Text>
            </>
          )}
          {mls.additional_mls && (
            <>
              <Text strong>Additional MLS:</Text>
              <Text>{mls.additional_mls}</Text>
            </>
          )}
          {mls.mls_number && (
            <>
              <Text strong>MLS #:</Text>
              <Text>{mls.mls_number}</Text>
            </>
          )}
          {mls.member_type && (
            <>
              <Text strong>Member Type:</Text>
              <Text>{mls.member_type}</Text>
            </>
          )}
          {mls.office_id && (
            <>
              <Text strong>Office ID:</Text>
              <Text>{mls.office_id}</Text>
            </>
          )}
          {mls.broker_owner_name && (
            <>
              <Text strong>MLS Contact:</Text>
              <Text>{mls.broker_owner_name}</Text>
            </>
          )}
          {mls.mls_contact_email && (
            <>
              <Text strong>MLS Contact Email:</Text>
              <Text>{mls.mls_contact_email}</Text>
            </>
          )}
          {mls.mls_login_url && (
            <>
              <Text strong>MLS Login URL:</Text>
              <Text>{mls.mls_login_url}</Text>
            </>
          )}
        </div>
      </Card>
    );
  };

  const renderSetupMode = () => {
    if (!user || !clientId) {
      return null;
    }
    return (
      <>
        <Steps
          current={currentStep}
          items={WIZARD_STEPS}
          onChange={step => {
            if (step < currentStep) {
              setCurrentStep(step);
            }
          }}
          style={{ marginBottom: 32 }}
        />

        <Form form={form} layout="vertical" size="large">
          {currentStep === 0 && (
            <Card
              extra={
                <Button
                  loading={prefilling}
                  onClick={handlePrefillFromHubSpot}
                  size="small"
                >
                  Prefill from HubSpot
                </Button>
              }
              style={{ marginBottom: 24 }}
              title="Business Information"
            >
              <BusinessInfoForm form={form} />
            </Card>
          )}

          {currentStep === 1 && (
            <Card
              style={{ marginBottom: 24 }}
              title="Authorized Representative"
            >
              <AuthorizedRepForm form={form} />
            </Card>
          )}

          {currentStep === 2 && (
            <Card style={{ marginBottom: 24 }} title="Campaign Configuration">
              <CampaignInfoForm form={form} />
            </Card>
          )}

          {currentStep === 3 && (
            <>
              <Card style={{ marginBottom: 24 }} title="Screenshot">
                <ScreenshotUpload
                  accessToken={user.accessToken}
                  apiUrl={apiUrl}
                  clientId={clientId}
                  existingUrl={screenshotUrl}
                  onUploaded={url => {
                    setScreenshotUrl(url);
                    form.setFieldValue(
                      ['campaign_info', 'screenshot_url'],
                      url,
                    );
                  }}
                />
              </Card>
              {screenshotUrl ? (
                <ComplianceCheckPanel
                  loading={complianceLoading}
                  onRunCheck={handleComplianceCheck}
                  result={complianceResult}
                />
              ) : (
                <Alert
                  message="Upload a screenshot of the opt-in form to enable the compliance check."
                  showIcon
                  style={{ marginBottom: 24 }}
                  type="info"
                />
              )}
            </>
          )}

          {currentStep === 4 && (
            <Card style={{ marginBottom: 24 }} title="Review Configuration">
              <Paragraph type="secondary">
                Review your entries before submitting. You can go back to any
                step to make changes.
              </Paragraph>
              <div
                style={{
                  background: '#fafafa',
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <Title level={5}>Business Information</Title>
                <Text strong>Legal Entity: </Text>
                <Text>
                  {form.getFieldValue(['business_info', 'legal_entity_name'])}
                </Text>
                <br />
                <Text strong>Brand: </Text>
                <Text>
                  {form.getFieldValue(['business_info', 'brand_name'])}
                </Text>
                <br />
                <Text strong>EIN: </Text>
                <Text>{form.getFieldValue(['business_info', 'ein'])}</Text>
                <br />
                <Text strong>Website: </Text>
                <Text>
                  {form.getFieldValue(['business_info', 'business_website'])}
                </Text>
                <br />
                <br />
                <Title level={5}>Authorized Representative</Title>
                <Text strong>Name: </Text>
                <Text>
                  {form.getFieldValue(['authorized_rep', 'first_name'])}{' '}
                  {form.getFieldValue(['authorized_rep', 'last_name'])}
                </Text>
                <br />
                <Text strong>Email: </Text>
                <Text>{form.getFieldValue(['authorized_rep', 'email'])}</Text>
                <br />
                <Text strong>Phone: </Text>
                <Text>{form.getFieldValue(['authorized_rep', 'phone'])}</Text>
                <br />
                <br />
                <Title level={5}>Campaign</Title>
                <Text strong>Form URL: </Text>
                <Text>{form.getFieldValue(['campaign_info', 'form_url'])}</Text>
                <br />
                <Text strong>Client Website: </Text>
                <Text>
                  {form.getFieldValue(['campaign_info', 'client_website'])}
                </Text>
                <br />
                <Text strong>Area Code: </Text>
                <Text>{form.getFieldValue('area_code')}</Text>
                <br />
                {screenshotUrl && (
                  <>
                    <Text strong>Screenshot: </Text>
                    <Text type="success">Uploaded</Text>
                    <br />
                  </>
                )}
                {complianceResult && (
                  <>
                    <Text strong>Compliance: </Text>
                    <Text>
                      {complianceResult.overall_status === 'ready'
                        ? 'Passed'
                        : complianceResult.overall_status === 'blocked'
                          ? `Blocked (${complianceResult.failures.length} issues)`
                          : 'Needs review'}
                    </Text>
                  </>
                )}
              </div>
            </Card>
          )}

          {currentStep === 5 && (
            <Card style={{ marginBottom: 24 }} title="Submit Onboarding">
              <Paragraph>
                Review your configuration and click "Start Onboarding" to
                submit. The pipeline will create a Trust Hub profile and begin
                the approval process.
              </Paragraph>

              <div
                style={{
                  background: '#fafafa',
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                <Title level={5}>Summary</Title>
                <Text strong>Business: </Text>
                <Text>
                  {form.getFieldValue(['business_info', 'brand_name'])} (
                  {form.getFieldValue(['business_info', 'legal_entity_name'])})
                </Text>
                <br />
                <Text strong>Contact: </Text>
                <Text>
                  {form.getFieldValue(['authorized_rep', 'first_name'])}{' '}
                  {form.getFieldValue(['authorized_rep', 'last_name'])} —{' '}
                  {form.getFieldValue(['authorized_rep', 'email'])}
                </Text>
                <br />
                <Text strong>Website: </Text>
                <Text>
                  {form.getFieldValue(['campaign_info', 'client_website'])}
                </Text>
                <br />
                <Text strong>Area Code: </Text>
                <Text>{form.getFieldValue('area_code')}</Text>
                <br />
                {complianceResult && (
                  <>
                    <Text strong>Compliance: </Text>
                    <Text>
                      {complianceResult.overall_status === 'ready'
                        ? 'Passed'
                        : complianceResult.overall_status === 'blocked'
                          ? `Blocked (${complianceResult.failures.length} issues)`
                          : 'Needs review'}
                    </Text>
                  </>
                )}
              </div>

              <Button
                loading={submitting}
                onClick={handleSubmit}
                size="large"
                style={{ backgroundColor: '#5b1eaf', borderColor: '#5b1eaf' }}
                type="primary"
              >
                Start Onboarding
              </Button>
            </Card>
          )}
        </Form>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
          }}
        >
          <Button disabled={currentStep === 0} onClick={handlePrev}>
            Previous
          </Button>
          {getNextButtonLabel(currentStep) ? (
            <Button
              onClick={handleNext}
              style={{ backgroundColor: '#5b1eaf', borderColor: '#5b1eaf' }}
              type="primary"
            >
              {getNextButtonLabel(currentStep)}
            </Button>
          ) : (
            <div />
          )}
        </div>

        {mlsInfo && renderMlsInfoSection(mlsInfo)}
      </>
    );
  };

  const handleAddInboundNumber = async () => {
    if (!user || !numericClientId) {
      return;
    }
    if (!inboundLeadSource || !inboundAreaCode) {
      message.error('Please fill in both fields.');
      return;
    }
    setAddingNumber(true);
    try {
      await addInboundNumber(apiUrl, user.accessToken, {
        client_id: numericClientId,
        lead_source: inboundLeadSource,
        area_code: inboundAreaCode,
      });
      message.success('Inbound number added successfully!');
      setInboundLeadSource('');
      setInboundAreaCode('');
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Failed to add inbound number',
      );
    } finally {
      setAddingNumber(false);
    }
  };

  const renderMonitorMode = () => {
    if (!onboardingStatus || !user || !clientId) {
      return null;
    }

    return (
      <>
        <OnboardingStatusTracker
          accessToken={user.accessToken}
          apiUrl={apiUrl}
          clientId={clientId}
          onStatusUpdate={setOnboardingStatus}
          status={onboardingStatus}
        />

        {onboardingStatus.status === 'rejected' && (
          <RejectionRecoveryPanel
            accessToken={user.accessToken}
            apiUrl={apiUrl}
            clientId={clientId}
            onStatusUpdate={setOnboardingStatus}
            status={onboardingStatus}
          />
        )}

        {onboardingStatus.status === 'active' && (
          <Card style={{ marginBottom: 24 }} title="Add Inbound Number">
            <Paragraph type="secondary">
              Add tracking numbers for lead source attribution (e.g., Zillow,
              Realtor.com).
            </Paragraph>
            <Space>
              <Input
                onChange={e => setInboundLeadSource(e.target.value)}
                placeholder="Lead source (e.g., zillow)"
                style={{ width: 200 }}
                value={inboundLeadSource}
              />
              <Input
                onChange={e => setInboundAreaCode(e.target.value)}
                placeholder="Area code (e.g., 954)"
                style={{ width: 150 }}
                value={inboundAreaCode}
              />
              <Button
                loading={addingNumber}
                onClick={handleAddInboundNumber}
                style={{ backgroundColor: '#5b1eaf', borderColor: '#5b1eaf' }}
                type="primary"
              >
                Add Number
              </Button>
            </Space>
          </Card>
        )}

        {mlsInfo && renderMlsInfoSection(mlsInfo)}
      </>
    );
  };

  return (
    <>
      <AuthenticatedTemplate>
        <ClientNavigation
          activeTab="twilio"
          clientId={clientId}
          clientName={clientConfig?.friendly_name}
          crmType={clientConfig?.crm_type as CrmType}
          numericClientId={numericClientId}
        />

        <Title level={3} style={{ marginBottom: 24, color: '#333' }}>
          Twilio A2P 10DLC Onboarding
        </Title>

        {loading && (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        )}

        {error && (
          <Alert
            closable
            description={error}
            message="Error"
            onClose={() => setError(undefined)}
            showIcon
            style={{ marginBottom: 24 }}
            type="error"
          />
        )}

        {!loading && !error && mode === 'setup' && renderSetupMode()}
        {!loading && !error && mode === 'monitor' && renderMonitorMode()}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <SignInButton />
      </UnauthenticatedTemplate>
    </>
  );
}
